import React, { useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useLanguage } from '../context/LanguageContext';
import {
  OWNER_PAIN_IDS,
  CLIENT_PAIN_IDS,
  PAIN_I18N_KEYS,
  averagePainRatings,
} from '../utils/surveyResponseHelpers';

const NAVY = '#14213d';
const GOLD = '#C9A84C';
const CHART_HEIGHT = 280;

function PainTooltip({ active, payload, t }) {
  if (!active || !payload?.length) return null;
  const item = payload[0]?.payload;
  return (
    <div
      className="rounded-xl border px-3 py-2 text-sm shadow-xl"
      style={{ background: NAVY, borderColor: `${GOLD}66`, color: '#e8ecf1' }}
    >
      <p className="m-0 font-semibold" style={{ color: GOLD }}>{item?.label}</p>
      <p className="m-0 mt-1">{t('surveyPainAvg').replace('{avg}', item?.avg ?? '—')}</p>
      <p className="m-0 text-xs opacity-70">n={item?.count}</p>
    </div>
  );
}

export default function SurveyPainChart({ rows }) {
  const { t } = useLanguage();
  const [mode, setMode] = useState('owner');
  const chartData = useMemo(() => {
    const ids = mode === 'owner' ? OWNER_PAIN_IDS : CLIENT_PAIN_IDS;
    const getLabel = (id) => t(PAIN_I18N_KEYS[id] || id);
    return averagePainRatings(rows, ids, getLabel).filter((d) => d.count > 0);
  }, [rows, mode, t]);

  const modeLabel = mode === 'owner' ? t('surveyOwner') : t('surveyClient');

  return (
    <div
      className="rounded-2xl border p-4 sm:p-5"
      style={{ background: NAVY, borderColor: `${GOLD}40` }}
    >
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h3 className="m-0 text-sm font-bold uppercase tracking-wider" style={{ color: GOLD }}>
          {t('surveyPainTitle')}
        </h3>
        <div className="flex gap-2">
          {['owner', 'client'].map((key) => (
            <button
              key={key}
              type="button"
              onClick={() => setMode(key)}
              className="rounded-full px-3 py-1.5 text-xs font-semibold transition"
              style={{
                border: `1px solid ${mode === key ? GOLD : 'rgba(255,255,255,0.2)'}`,
                background: mode === key ? `${GOLD}33` : 'transparent',
                color: mode === key ? GOLD : '#8896a8',
              }}
            >
              {key === 'owner' ? t('surveyOwner') : t('surveyClient')}
            </button>
          ))}
        </div>
      </div>
      {chartData.length === 0 ? (
        <p className="py-12 text-center text-sm text-[#8896a8]">
          {t('surveyPainNoData').replace('{mode}', modeLabel)}
        </p>
      ) : (
        <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
          <BarChart data={chartData} margin={{ top: 12, right: 8, left: 0, bottom: 8 }}>
            <CartesianGrid stroke="rgba(201,168,76,0.12)" vertical={false} />
            <XAxis
              dataKey="label"
              tick={{ fill: '#8896a8', fontSize: 11 }}
              axisLine={{ stroke: 'rgba(201,168,76,0.2)' }}
            />
            <YAxis
              domain={[0, 5]}
              ticks={[1, 2, 3, 4, 5]}
              tick={{ fill: '#8896a8', fontSize: 11 }}
              axisLine={{ stroke: 'rgba(201,168,76,0.2)' }}
            />
            <Tooltip content={<PainTooltip t={t} />} cursor={{ fill: 'rgba(201,168,76,0.08)' }} />
            <Bar dataKey="avg" radius={[6, 6, 0, 0]} maxBarSize={48}>
              {chartData.map((entry) => (
                <Cell key={entry.id} fill={GOLD} />
              ))}
              <LabelList dataKey="avg" position="top" fill={GOLD} fontSize={12} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}
